import { Link } from 'react-router-dom';
import { useFiscalDashboard, type FiscalCounts } from '@/hooks/useFiscal';
import { getDocumentTypeName } from '@/lib/fiscal/documentTypes';
import { formatDateTime } from '@/lib/format';

interface StatCard {
  label: string;
  value: number;
  icon: string;
  tone: 'neutral' | 'ok' | 'warn' | 'bad';
  hint: string;
}

const TONE_META = {
  neutral: { badge: 'bg-background-100 text-foreground-700', dot: 'bg-foreground-400' },
  ok: { badge: 'bg-emerald-100 text-emerald-800', dot: 'bg-emerald-500' },
  warn: { badge: 'bg-amber-100 text-amber-800', dot: 'bg-amber-500' },
  bad: { badge: 'bg-red-100 text-red-800', dot: 'bg-red-500' },
} as const;

function buildStatCards(c: FiscalCounts): StatCard[] {
  return [
    { label: 'e-CF emitidos hoy', value: c.emitidosHoy, icon: 'ri-file-list-3-line', tone: 'neutral', hint: 'Comprobantes creados hoy' },
    { label: 'Pendientes de envío', value: c.pendientes, icon: 'ri-hourglass-line', tone: 'warn', hint: 'Por generar XML / firmar' },
    { label: 'Enviados a DGII', value: c.enviados, icon: 'ri-send-plane-line', tone: 'neutral', hint: 'En tránsito o recibidos' },
    { label: 'Aceptados', value: c.aceptados, icon: 'ri-checkbox-circle-line', tone: 'ok', hint: 'Comprobantes válidos' },
    { label: 'Rechazados', value: c.rechazados, icon: 'ri-close-circle-line', tone: 'bad', hint: 'Rechazos de DGII' },
    { label: 'Errores', value: c.errores, icon: 'ri-error-warning-line', tone: 'bad', hint: 'Fallos de procesamiento' },
    { label: 'Contingencias', value: c.contingencias, icon: 'ri-cloud-off-line', tone: 'warn', hint: 'Por sincronizar' },
  ];
}

const SERVICE_STATUS = [
  { label: 'Base de datos', value: 'Operativa', icon: 'ri-database-2-line', ok: true },
  { label: 'Cola de envío', value: 'Lista', icon: 'ri-stack-line', ok: true },
  { label: 'DGII', value: 'No configurado', icon: 'ri-government-line', ok: false },
  { label: 'Certificado digital', value: 'No configurado', icon: 'ri-shield-keyhole-line', ok: false },
  { label: 'Firma digital', value: 'No configurado', icon: 'ri-fingerprint-line', ok: false },
];

export default function FiscalDashboardPage() {
  const { documents, counts, loading, error, reload } = useFiscalDashboard();
  const cards = buildStatCards(counts);
  const recent = documents.slice(0, 8);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            Facturación Electrónica
          </h1>
          <p className="mt-1 text-foreground-600">
            Panel de control de Comprobantes Fiscales Electrónicos (e-CF).
          </p>
        </div>
        <Link
          to="/fiscal/errors"
          className="inline-flex items-center justify-center gap-2 rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
        >
          <i className="ri-error-warning-line" /> Centro de errores
        </Link>
      </div>

      {/* Aviso de certificación */}
      <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
        <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
          <i className="ri-shield-check-line text-lg" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground-950">
            Preparado para certificación DGII
          </p>
          <p className="text-sm text-foreground-600">
            El sistema está preparado técnicamente para el proceso de certificación. Aún no se ha
            obtenido la autorización oficial de la DGII.
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50 border border-background-300 px-3 py-1 text-xs font-semibold text-foreground-700 whitespace-nowrap">
          <i className="ri-flask-line" /> Ambiente: Certificación
        </span>
      </div>

      {error && (
        <div className="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 flex items-center gap-3">
          <i className="ri-error-warning-line text-red-600" />
          <p className="text-sm text-foreground-700">{error}</p>
          <button
            onClick={reload}
            className="ml-auto text-sm font-medium text-red-700 hover:underline cursor-pointer whitespace-nowrap"
          >
            Reintentar
          </button>
        </div>
      )}

      {loading ? (
        <div className="mt-10 flex items-center justify-center py-20">
          <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
        </div>
      ) : (
        <>
          {/* Indicadores */}
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3">
            {cards.map((card) => (
              <div key={card.label} className="rounded-lg border border-background-200 bg-background-50 p-4">
                <div className="flex items-center justify-between">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-background-100 text-foreground-700">
                    <i className={`${card.icon} text-lg`} />
                  </div>
                  <span className={`w-2.5 h-2.5 rounded-full ${TONE_META[card.tone].dot}`} />
                </div>
                <p className="mt-3 text-2xl font-heading font-bold text-foreground-950">{card.value}</p>
                <p className="mt-0.5 text-xs font-medium text-foreground-800">{card.label}</p>
                <p className="mt-1 text-[11px] text-foreground-500">{card.hint}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Estado del servicio */}
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-lg font-semibold text-foreground-950 flex items-center gap-2">
                <i className="ri-pulse-line text-primary-600" /> Estado del servicio
              </h2>
              <ul className="mt-4 divide-y divide-background-100">
                {SERVICE_STATUS.map((s) => (
                  <li key={s.label} className="flex items-center justify-between py-3">
                    <span className="flex items-center gap-3 text-sm text-foreground-800">
                      <i className={`${s.icon} text-lg text-foreground-500`} />
                      {s.label}
                    </span>
                    <span
                      className={`inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-semibold whitespace-nowrap ${
                        s.ok
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-amber-100 text-amber-800'
                      }`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${s.ok ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                      {s.value}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-xs text-foreground-500">
                La comunicación con DGII, la firma digital y el certificado se activan en la fase de
                integración de servicios web.
              </p>
            </div>

            {/* Últimos comprobantes */}
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-lg font-semibold text-foreground-950 flex items-center gap-2">
                  <i className="ri-receipt-line text-primary-600" /> Últimos comprobantes
                </h2>
                {documents.length > 0 && (
                  <span className="text-xs text-foreground-500">{documents.length} registrados</span>
                )}
              </div>
              {recent.length === 0 ? (
                <p className="mt-4 text-sm text-foreground-600">
                  Aún no hay comprobantes electrónicos. Al cobrar en el POS se genera el e-CF y su
                  e-NCF automáticamente.
                </p>
              ) : (
                <ul className="mt-4 divide-y divide-background-100">
                  {recent.map((d) => (
                    <li key={d.id} className="flex items-center justify-between py-2.5 gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground-950 truncate">
                          {d.e_ncf ?? 'Sin e-NCF'}
                        </p>
                        <p className="text-xs text-foreground-500 truncate">
                          {getDocumentTypeName(d.document_type)} · {formatDateTime(d.created_at)}
                        </p>
                      </div>
                      <span className="text-sm font-medium text-foreground-700 shrink-0">
                        {d.fiscal_status}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}